import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Location, DropDown } from '../../../assests/ExportImageIcon'
import SearchableSelect from '../../../componants/searchableSelect/SearchableSelect'
import DatePicker from '../../../componants/datePicker/DatePicker'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { showToast } from '../../../componants/toast/toastService'
import { setEventBookingGuests, setEventBookingResult, setSelectedEvent } from '../../../redux/eventBooking/eventBookingSlice'
import './eventBookingDetail.css'

const IMPORTANT_INFO = [
    'Carry a valid government ID proof for entry verification',
    'Arrive at least 30 minutes before the scheduled start time',
    'Tickets are linked to the guest details provided at booking',
    "Refund eligibility depends on the event's cancellation policy",
]

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const isValidEmail = (email) => EMAIL_REGEX.test(String(email || '').trim())
const isValidPhone = (phone) => /^\d{10}$/.test(String(phone || '').replace(/\D/g, ''))

/** DOB cannot be today or a future date — max selectable is yesterday */
const maxDobIso = () => {
    const date = new Date()
    date.setDate(date.getDate() - 1)
    return date.toISOString().split('T')[0]
}

const isValidDob = (dob) => {
    if (!dob) return false
    return String(dob) <= maxDobIso()
}

const getAge = (dob) => {
    const date = new Date(`${dob}T00:00:00`)
    if (Number.isNaN(date.getTime())) return null
    const today = new Date()
    let age = today.getFullYear() - date.getFullYear()
    const monthDiff = today.getMonth() - date.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) age -= 1
    return age
}

const emptyGuest = () => ({
    first_name: '', last_name: '', email: '', phone: '', gender: '', dob: '', nationality: '',
})

const parseFullName = (raw) => {
    const parts = String(raw || '').trim().split(/\s+/).filter(Boolean)
    if (!parts.length) return null
    return { first_name: parts[0], last_name: parts.slice(1).join(' ') }
}

const parseEventPrefill = (searchParams) => {
    const phone = String(searchParams.get('phone') || '').replace(/\D/g, '').slice(0, 10)
    const email = String(searchParams.get('email') || '').trim()
    let guests = []
    const rawData = searchParams.get('guest_data')
    if (rawData) {
        try {
            const parsed = JSON.parse(rawData)
            if (Array.isArray(parsed)) {
                guests = parsed.map((item) => ({
                    ...emptyGuest(),
                    first_name: item.first_name || parseFullName(item.name)?.first_name || '',
                    last_name: item.last_name || parseFullName(item.name)?.last_name || '',
                    email: item.email || email,
                    phone: String(item.phone || phone || '').replace(/\D/g, '').slice(0, 10),
                    gender: item.gender || '',
                    dob: item.dob || '',
                    nationality: item.nationality || '',
                }))
            }
        } catch {
            guests = []
        }
    }
    if (!guests.length) {
        const guestsRaw = searchParams.get('guests') || searchParams.get('guest_names') || ''
        guests = guestsRaw
            .split(/[|,]/)
            .map(parseFullName)
            .filter(Boolean)
            .map((guest) => ({
                ...emptyGuest(),
                ...guest,
                email,
                phone,
            }))
    }
    const qtyRaw = Number(searchParams.get('qty') || guests.length || 0)
    const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : guests.length
    return { guests, phone, email, qty }
}

const isGuestValid = (guest, minimumAge) => {
    if (
        !guest.first_name?.trim()
        || !guest.last_name?.trim()
        || !isValidEmail(guest.email)
        || !isValidPhone(guest.phone)
        || !guest.gender
        || !isValidDob(guest.dob)
    ) return false
    if (minimumAge > 0) {
        const age = getAge(guest.dob)
        if (age == null || age < minimumAge) return false
    }
    return true
}

const FieldError = ({ children }) => (
    children ? <p className='hotelGuestFieldError'>{children}</p> : null
)

const formatScheduleDate = (schedule) => {
    if (!schedule?.event_date) return { day: '--', month: '', weekday: '' }
    const date = new Date(`${schedule.event_date}T00:00:00`)
    if (Number.isNaN(date.getTime())) return { day: schedule.event_date, month: '', weekday: '' }
    return {
        day: date.toLocaleDateString('en-US', { day: '2-digit' }),
        month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
        weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
    }
}

const formatTime = (value) => {
    if (!value) return ''
    const [hh, mm] = value.split(':')
    const hour = Number(hh)
    const period = hour >= 12 ? 'PM' : 'AM'
    const displayHour = ((hour + 11) % 12) + 1
    return `${displayHour}:${mm} ${period}`
}

// Show the exact amount the API returned — never round it away.
const formatRupees = (value) => {
    const num = Number(value)
    if (Number.isNaN(num)) return '—'
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const EventBookingDetail = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [searchParams] = useSearchParams()
    const eventIdFromUrl = searchParams.get('event_id')
    const urlPrefill = useMemo(() => parseEventPrefill(searchParams), [searchParams])
    const event = useSelector((state) => state.eventBooking.selectedEvent)
    const [loadingEvent, setLoadingEvent] = useState(Boolean(eventIdFromUrl && !event))

    const tickets = useMemo(() => event?.tickets || [], [event])
    const schedules = useMemo(() => event?.schedules || [], [event])
    const venue = event?.event_venues?.[0]

    const [scheduleId, setScheduleId] = useState(null)
    const [selectedDate, setSelectedDate] = useState(null)
    const [selectedTicketId, setSelectedTicketId] = useState(null)

    // One date chip per distinct event_date — a date can have several schedules
    // (times), shown as the separate "Choose Time" row below once picked.
    const uniqueDateSchedules = useMemo(() => {
        const seen = new Set()
        return schedules.filter((schedule) => {
            if (!schedule.event_date || seen.has(schedule.event_date)) return false
            seen.add(schedule.event_date)
            return true
        })
    }, [schedules])

    const selectedDateSchedules = useMemo(
        () => schedules.filter((schedule) => schedule.event_date === selectedDate),
        [schedules, selectedDate]
    )
    const [ticketQty, setTicketQty] = useState(1)
    const [guests, setGuests] = useState([])
    const [booking, setBooking] = useState(false)
    const [convenienceFeeAmount, setConvenienceFeeAmount] = useState(0)
    const [amountAfterFee, setAmountAfterFee] = useState(null)
    const [feeLoading, setFeeLoading] = useState(false)

    const [availableCoupons, setAvailableCoupons] = useState([])
    const [couponCodeDraft, setCouponCodeDraft] = useState('')
    const [appliedCoupon, setAppliedCoupon] = useState(null)
    // Tracks which specific coupon code is mid-validation — not a shared
    // boolean, otherwise every coupon card's button flips to "Applying..."
    // at once when only one was actually clicked.
    const [applyingCode, setApplyingCode] = useState(null)

    useEffect(() => {
        const eventId = eventIdFromUrl || event?.id
        if (!eventId) return
        let cancelled = false
        setLoadingEvent(true)
        service
            .get(endpoints.customerEvents.detail.replace('{event_id}', eventId), {}, { silent: true })
            .then(({ data }) => {
                if (!cancelled && data?.data) dispatch(setSelectedEvent(data.data))
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setLoadingEvent(false)
            })
        return () => {
            cancelled = true
        }
    }, [eventIdFromUrl, event?.id, dispatch])

    useEffect(() => {
        service
            .get(endpoints.bookings.coupons, { service: 'event', limit: 12 }, { silent: true })
            .then(({ data }) => setAvailableCoupons(data?.data?.coupons || []))
            .catch(() => setAvailableCoupons([]))
    }, [])

    useEffect(() => {
        if (uniqueDateSchedules.length) setSelectedDate((prev) => prev || uniqueDateSchedules[0].event_date)
    }, [uniqueDateSchedules])

    useEffect(() => {
        if (selectedDateSchedules.length === 1) {
            setScheduleId(selectedDateSchedules[0].id)
        } else if (scheduleId && !selectedDateSchedules.some((schedule) => schedule.id === scheduleId)) {
            setScheduleId(null)
        }
    }, [selectedDateSchedules, scheduleId])

    // Booking is single ticket-type at a time — selecting a different ticket resets quantity to 1.
    useEffect(() => {
        if (tickets.length === 1) setSelectedTicketId(tickets[0].id)
    }, [tickets])

    const selectedTicket = tickets.find((ticket) => ticket.id === selectedTicketId) || null
    const selectedTicketMaxQty = selectedTicket
        ? Math.max(0, Math.min(selectedTicket.max_per_booking || 10, selectedTicket.available_seats ?? 10))
        : 0

    useEffect(() => {
        if (!urlPrefill.qty || !selectedTicketMaxQty) return
        setTicketQty(Math.max(1, Math.min(selectedTicketMaxQty, urlPrefill.qty)))
    }, [urlPrefill.qty, selectedTicketMaxQty])

    const selectTicket = (ticket) => {
        setSelectedTicketId(ticket.id)
        setTicketQty(1)
    }

    const updateTicketQty = (delta) => {
        setTicketQty((prev) => Math.max(1, Math.min(selectedTicketMaxQty, prev + delta)))
    }

    const guestSlots = useMemo(() => (
        selectedTicket
            ? Array.from({ length: ticketQty }, () => ({ ticketId: selectedTicket.id, ticketName: selectedTicket.name }))
            : []
    ), [selectedTicket, ticketQty])

    useEffect(() => {
        setGuests((prev) => guestSlots.map((_, index) => {
            const existing = prev[index]
            if (existing && (existing.first_name || existing.last_name || existing.email || existing.phone)) {
                return existing
            }
            const named = urlPrefill.guests[index] || emptyGuest()
            return {
                ...emptyGuest(),
                ...named,
                email: named.email || urlPrefill.email || '',
                phone: named.phone || urlPrefill.phone || '',
                gender: named.gender || '',
                dob: named.dob || '',
            }
        }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guestSlots.length])

    const updateGuestField = (index, field, value) => {
        let nextValue = value
        if (field === 'phone') nextValue = String(value || '').replace(/\D/g, '').slice(0, 10)
        setGuests((prev) => {
            const next = [...prev]
            next[index] = { ...next[index], [field]: nextValue }
            return next
        })
    }

    const totalQuantity = guestSlots.length
    const netAmount = selectedTicket ? (Number(selectedTicket.price) || 0) * ticketQty : 0

    // Recalculate via POST /bookings/convenience_fee/calculation whenever the
    // selected ticket/quantity changes — the fee depends on the running total.
    const feeType = event?.convenience_fee?.fee_type
    useEffect(() => {
        if (!netAmount || !feeType) {
            setConvenienceFeeAmount(0)
            setAmountAfterFee(null)
            setFeeLoading(false)
            return undefined
        }

        let cancelled = false
        setFeeLoading(true)
        service
            .post(endpoints.bookings.convenienceFeeCalculation, {
                total_amount: String(netAmount),
                currency: 'INR',
                fee_type: feeType,
                value: String(event.convenience_fee.value),
                ...(event.convenience_fee.max_cap != null ? { max_cap: String(event.convenience_fee.max_cap) } : {}),
            }, { silent: true })
            .then(({ data }) => {
                if (cancelled) return
                // Keep the API's own numbers exactly as returned when present — but a
                // successful response with a null/missing/0 fee just means "no fee",
                // not "unresolved". Only a failed request should block checkout.
                setConvenienceFeeAmount(data?.data?.convenience_fee_amount ?? 0)
                setAmountAfterFee(data?.data?.amount_after_fee ?? null)
            })
            .catch(() => {
                if (!cancelled) {
                    setConvenienceFeeAmount(null)
                    setAmountAfterFee(null)
                }
            })
            .finally(() => {
                if (!cancelled) setFeeLoading(false)
            })

        return () => { cancelled = true }
    }, [netAmount, feeType, event?.convenience_fee?.value, event?.convenience_fee?.max_cap])

    // Unknown (fetch failed) or still loading — don't quote a total that's missing a real fee.
    const feeUnresolved = Boolean(netAmount && feeType) && (feeLoading || convenienceFeeAmount == null)
    const preDiscountTotal = amountAfterFee != null ? Number(amountAfterFee) : netAmount + Number(convenienceFeeAmount || 0)

    // A coupon was validated against a specific ticket total — if the ticket/quantity
    // changes afterwards, the discount is stale and must be re-validated.
    useEffect(() => {
        setAppliedCoupon((prev) => (prev && prev.validatedNetAmount !== netAmount ? null : prev))
    }, [netAmount])

    const discountAmount = appliedCoupon ? Number(appliedCoupon.discountAmount) || 0 : 0
    const totalAmount = feeUnresolved ? null : Math.max(0, preDiscountTotal - discountAmount)

    const handleApplyCoupon = (code) => {
        const codeToApply = (code || couponCodeDraft).trim()
        if (!codeToApply) {
            showToast('Please enter a coupon code', 'error')
            return
        }
        if (!netAmount) {
            showToast('Select a ticket before applying a coupon', 'error')
            return
        }
        if (applyingCode) return
        setApplyingCode(codeToApply)
        service
            .post(endpoints.bookings.couponsValidate, {
                code: codeToApply,
                amount: String(netAmount),
                currency: 'INR',
                service: 'event',
            })
            .then(({ data }) => {
                const result = data?.data
                if (!result?.valid) {
                    showToast('This coupon is not valid', 'error')
                    return
                }
                setAppliedCoupon({
                    code: result.code || codeToApply,
                    discountAmount: result.discount_amount,
                    payableAmount: result.payable_amount,
                    originalAmount: result.original_amount,
                    validatedNetAmount: netAmount,
                })
                setCouponCodeDraft('')
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setApplyingCode(null))
    }

    const handleRemoveCoupon = () => {
        // Nothing was ever created server-side by /validate — clearing local state is enough.
        setAppliedCoupon(null)
    }

    const minimumAge = Number(event?.minimum_age) || 0
    const guestsValid = totalQuantity > 0 && guests.length === totalQuantity && guests.every((guest) => isGuestValid(guest, minimumAge))
    const scheduleValid = schedules.length <= 1 || !!scheduleId
    const canBook = guestsValid && scheduleValid && !booking && !feeUnresolved

    const handleBook = async () => {
        if (!canBook || !event) return

        const items = selectedTicket ? [{ event_ticket_id: selectedTicket.id, quantity: ticketQty }] : []

        const contactInfo = guests.map((guest) => ({
            first_name: guest.first_name.trim(),
            last_name: guest.last_name.trim(),
            email: guest.email.trim(),
            phone_number: guest.phone.trim(),
            gender: guest.gender,
            dob: guest.dob,
            ...(guest.nationality?.trim() ? { nationality: guest.nationality.trim() } : {}),
        }))

        const payload = {
            event_id: event.id,
            ...(scheduleId ? { schedule_id: scheduleId } : {}),
            items,
            contact_info: contactInfo,
            ...(appliedCoupon ? { coupon_code: appliedCoupon.code } : {}),
            net_amount: String(netAmount),
            convenience_fee: String(convenienceFeeAmount || 0),
            discount_amount: String(discountAmount),
            total_amount: String(totalAmount),
        }

        setBooking(true)
        try {
            const { data } = await service.post(endpoints.customerEvents.book, payload)
            dispatch(setEventBookingGuests(contactInfo))
            dispatch(setEventBookingResult(data.data))
            navigate('/event-payment')
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setBooking(false)
        }
    }

    if (!event && loadingEvent) {
        return (
            <section className='flightBookingDetails commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p>Loading event details…</p>
                </div>
            </section>
        )
    }

    if (!event) {
        return (
            <section className='flightBookingDetails commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p>No event selected. Please pick an event first.</p>
                    <button type='button' className='commonBtn' onClick={() => navigate('/event-booking')}>
                        Browse Events
                    </button>
                </div>
            </section>
        )
    }

    return (
        <>
            <section className='flightBookingDetails commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='allBookingDetail'>
                                <div className='allBokkingInfo'>
                                    <div className="hotelInfoCard">
                                        <div className='hotelInfoCard__top'>
                                            <div>
                                                <div className='hotelInfoCard__name'>{event.name}</div>
                                                <div className='hotelCard__dateTime'>{event.artist_name}</div>
                                            </div>
                                        </div>

                                        <div className='hotelInfoCard__divider' />

                                        <div className='hotelInfoCard__location'>
                                            <span className='hotelInfoCard__locationIcon'>
                                                <img src={Location} alt="" />
                                            </span>
                                            <div className='hotelCard__location'>
                                                <div>
                                                    <div className='hotelCard__locationName'>{venue?.venue_name || '—'}</div>
                                                    <div className='hotelCard__locationNote'>{venue?.address || ''}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {schedules.length > 0 && (
                                        <div className='travellerDetail overflow-hidden'>
                                            <div className='fareDetailsHead'>
                                                <div className='bsSectionTitle'>Select Date &amp; Time</div>
                                            </div>

                                            <div className="hotelInfoCard__divider"></div>

                                            <div className="dateTimeSection">
                                                <div className='eventDateTimeLabel'>Choose Date</div>
                                                <div className='stopsRow'>
                                                    {uniqueDateSchedules.length > 0 && (
                                                        <div className='monthCard'>{formatScheduleDate(uniqueDateSchedules[0]).month}</div>
                                                    )}
                                                    {uniqueDateSchedules.map((schedule) => {
                                                        const { day, weekday } = formatScheduleDate(schedule)
                                                        return (
                                                            <div
                                                                key={schedule.event_date}
                                                                className={`stopCard eventDateChip ${selectedDate === schedule.event_date ? 'active' : ''}`}
                                                                onClick={() => setSelectedDate(schedule.event_date)}
                                                            >
                                                                <div className='stopCardLabel dateCards'>{day}</div>
                                                                <div className='stopCardPrice'>{weekday}</div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>

                                            {selectedDateSchedules.length > 0 && (
                                                <div className="dateTimeSection">
                                                    <div className='eventDateTimeLabel'>Choose Time</div>
                                                    <div className='stopsRow'>
                                                        {selectedDateSchedules.map((schedule) => (
                                                            <button
                                                                type='button'
                                                                key={schedule.id}
                                                                className={`timeCard ${scheduleId === schedule.id ? 'active' : ''}`}
                                                                onClick={() => setScheduleId(schedule.id)}
                                                            >
                                                                {formatTime(schedule.start_time)}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className='travellerDetail'>
                                        <div className='fareDetailsHead'>
                                            <div className='bsSectionTitle'>Choose Ticket</div>
                                        </div>

                                        <div className="fareDetailsBody">
                                            {tickets.length ? tickets.map((ticket) => {
                                                const soldOut = (ticket.available_seats ?? 1) <= 0
                                                const isSelected = selectedTicketId === ticket.id
                                                return (
                                                    <div
                                                        className={`hotelInfoCard__top eventTicketRow eventTicketRow--selectable ${isSelected ? 'active' : ''} ${soldOut ? 'eventTicketRow--soldOut' : ''}`}
                                                        key={ticket.id}
                                                        onClick={() => !soldOut && selectTicket(ticket)}
                                                    >
                                                        <div className="ticketInformation">
                                                            <div className="eventTicketRow__head">
                                                                <h3 className="dateTimeHeading mb-1">{ticket.name}</h3>
                                                                {ticket.ticket_type && (
                                                                    <span className='eventTicketType'>{ticket.ticket_type}</span>
                                                                )}
                                                                {isSelected && <span className='eventTicketSelected'>Selected</span>}
                                                            </div>
                                                            <div className="travellerPassengerHead">
                                                                <span>{formatRupees(ticket.price)}</span>
                                                            </div>
                                                            {soldOut && <p className='bsFareNote'>Sold out</p>}
                                                        </div>

                                                        {isSelected ? (
                                                            <div className="cardTags" onClick={(e) => e.stopPropagation()}>
                                                                <button
                                                                    type="button"
                                                                    className="counterButton"
                                                                    onClick={() => updateTicketQty(-1)}
                                                                    disabled={ticketQty <= 1}
                                                                    aria-label="Decrease ticket quantity"
                                                                >
                                                                    −
                                                                </button>

                                                                <span className="ticketQuantity">{ticketQty}</span>

                                                                <button
                                                                    type="button"
                                                                    className="counterButton"
                                                                    onClick={() => updateTicketQty(1)}
                                                                    disabled={ticketQty >= selectedTicketMaxQty}
                                                                    aria-label="Increase ticket quantity"
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <span className={`fareTypeRadio ${isSelected ? 'checked' : ''}`} />
                                                        )}
                                                    </div>
                                                )
                                            }) : <p className='bsFareNote'>No tickets available for this event.</p>}
                                        </div>
                                    </div>

                                    <div className='travellerDetail'>
                                        <div className='fareDetailsHead'>
                                            <div className='bsSectionTitle'>Guest Details</div>
                                        </div>

                                        {totalQuantity > 0 ? (
                                            <div className='fareDetailsBody'>
                                                {guestSlots.map((slot, index) => {
                                                    const guest = guests[index] || emptyGuest()
                                                    return (
                                                        <div className='bsBox eventGuestCard' key={index}>
                                                            <div className='travellerPassengerHead'>
                                                                <span>Guest {index + 1}</span>
                                                                <span className='travellerPassengerType'>{slot.ticketName}</span>
                                                            </div>
                                                            <div className='travellerFieldRow'>
                                                                <div className='travellerField'>
                                                                    <label>First Name</label>
                                                                    <input
                                                                        type='text'
                                                                        value={guest.first_name}
                                                                        onChange={(e) => updateGuestField(index, 'first_name', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className='travellerField'>
                                                                    <label>Last Name</label>
                                                                    <input
                                                                        type='text'
                                                                        value={guest.last_name}
                                                                        onChange={(e) => updateGuestField(index, 'last_name', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className='travellerField'>
                                                                    <label>Gender</label>
                                                                    <SearchableSelect
                                                                        options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }]}
                                                                        value={guest.gender}
                                                                        onChange={(gender) => updateGuestField(index, 'gender', gender)}
                                                                        placeholder='Select'
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className='travellerFieldRow'>
                                                                <div className='travellerField'>
                                                                    <label>Email ID</label>
                                                                    <input
                                                                        type='email'
                                                                        placeholder='name@example.com'
                                                                        value={guest.email}
                                                                        onChange={(e) => updateGuestField(index, 'email', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className='travellerField'>
                                                                    <label>Phone Number</label>
                                                                    <div className='guestPhoneInput'>
                                                                        <span className='guestPhoneCode'>+91 <img src={DropDown} alt="" /></span>
                                                                        <span className='guestPhoneDivider' />
                                                                        <input
                                                                            type='tel'
                                                                            maxLength={10}
                                                                            placeholder='10-digit number'
                                                                            value={guest.phone}
                                                                            onChange={(e) => updateGuestField(index, 'phone', e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className='travellerFieldRow'>
                                                                <div className='travellerField'>
                                                                    <label>Date Of Birth (DOB)</label>
                                                                    <DatePicker
                                                                        className='travellerDateInput'
                                                                        max={maxDobIso()}
                                                                        value={guest.dob}
                                                                        onChange={(dob) => updateGuestField(index, 'dob', dob)}
                                                                    />
                                                                    {guest.dob && !isValidDob(guest.dob) ? (
                                                                        <FieldError>DOB cannot be today or a future date</FieldError>
                                                                    ) : guest.dob && minimumAge > 0 && (getAge(guest.dob) ?? 0) < minimumAge ? (
                                                                        <FieldError>Guest must be at least {minimumAge} years old for this event</FieldError>
                                                                    ) : null}
                                                                </div>
                                                                <div className='travellerField'>
                                                                    <label>Nationality (Optional)</label>
                                                                    <input
                                                                        type='text'
                                                                        value={guest.nationality}
                                                                        onChange={(e) => updateGuestField(index, 'nationality', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        ) : (
                                            <p className='bsFareNote fareDetailsBody'>Select at least one ticket above to add guest details.</p>
                                        )}
                                    </div>

                                    <div className='totalPay bgWhite paddingCommon20 borderRadius20 commonBorder'>
                                        <button type='button' className='commonBtn w-100' disabled={!canBook} onClick={handleBook}>
                                            {booking ? (
                                                <>
                                                    <span className='btnSpinner' aria-hidden="true" />
                                                    Booking…
                                                </>
                                            ) : feeLoading ? (
                                                'Calculating fee…'
                                            ) : (
                                                `Continue to Pay${totalAmount ? ` ${formatRupees(totalAmount)}` : ''}`
                                            )}
                                        </button>
                                        <p className='bsFareNote totalPayNote'>
                                            {feeUnresolved && !feeLoading
                                                ? "Couldn't calculate the convenience fee. Please try again."
                                                : canBook
                                                    ? 'By proceeding, you agree to our Terms & Conditions'
                                                    : 'Select tickets, date/time and complete guest details to continue'}
                                        </p>
                                    </div>
                                </div>
                                <div className='bookingSidebar'>
                                    <div className='bsSection'>
                                        <div className='bsSectionTitle'>Important Information</div>
                                        <div className='bsBox'>
                                            <ul className='bsInfoList'>
                                                {IMPORTANT_INFO.map((info, index) => (
                                                    <li key={index}>{info}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    {netAmount > 0 && (
                                        <div className='bsSection'>
                                            <div className='bsSectionTitle'>Offers &amp; Discounts</div>
                                            <div className='bsBox'>
                                                {appliedCoupon ? (
                                                    <div className='bsCouponCard'>
                                                        <div className='bsCouponHead'>
                                                            <span className='bsCouponCode'>{appliedCoupon.code}</span>
                                                            <span className='bsCouponOff'>{formatRupees(appliedCoupon.discountAmount)} off</span>
                                                        </div>
                                                        <button type='button' className='bsCouponApply' onClick={handleRemoveCoupon}>
                                                            REMOVE COUPON
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <input
                                                            type='text'
                                                            className='bsCouponInput'
                                                            placeholder='Enter coupon code'
                                                            value={couponCodeDraft}
                                                            onChange={(e) => setCouponCodeDraft(e.target.value)}
                                                            onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                                                        />
                                                        {availableCoupons.map((coupon, index) => {
                                                            const minAmount = Number(coupon.min_booking_amount) || 0
                                                            const belowMinimum = minAmount > 0 && netAmount < minAmount
                                                            return (
                                                                <div className='bsCouponCard' key={coupon.code || index}>
                                                                    <div className='bsCouponHead'>
                                                                        <span className='bsCouponCode'>{coupon.code}</span>
                                                                        <span className='bsCouponOff'>{coupon.discount_label}</span>
                                                                    </div>
                                                                    {coupon.terms && <p className='bsCouponDesc'>{coupon.terms}</p>}
                                                                    {belowMinimum && (
                                                                        <p className='bsCouponDesc'>Minimum order {coupon.currency || 'INR'} {minAmount.toLocaleString('en-IN')} required</p>
                                                                    )}
                                                                    <button
                                                                        type='button'
                                                                        className='bsCouponApply'
                                                                        disabled={!!applyingCode || belowMinimum}
                                                                        onClick={() => handleApplyCoupon(coupon.code)}
                                                                    >
                                                                        {applyingCode === coupon.code ? 'Applying...' : 'APPLY COUPON'}
                                                                    </button>
                                                                </div>
                                                            )
                                                        })}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className='bsSection'>
                                        <div className='bsSectionTitle'>Fare Breakup</div>
                                        <div className='bsBox'>
                                            {selectedTicket ? (
                                                <div className='bsFareRow'>
                                                    <span>{selectedTicket.name} × {ticketQty}</span>
                                                    <span>{formatRupees(netAmount)}</span>
                                                </div>
                                            ) : (
                                                <p className='bsFareNote'>No ticket selected yet.</p>
                                            )}
                                            {netAmount > 0 && feeType && (
                                                <div className='bsFareRow'>
                                                    <span>Convenience Fee</span>
                                                    <span>
                                                        {feeLoading
                                                            ? 'Calculating…'
                                                            : convenienceFeeAmount == null ? '—' : formatRupees(convenienceFeeAmount)}
                                                    </span>
                                                </div>
                                            )}
                                            {discountAmount > 0 && (
                                                <div className='bsFareRow bookingDiscountRow'>
                                                    <span>Discount ({appliedCoupon.code})</span>
                                                    <span>- {formatRupees(discountAmount)}</span>
                                                </div>
                                            )}
                                            <div className='bsFareDivider' />
                                            <div className='bsFareRow bsFareRow--total'>
                                                <span>TOTAL AMOUNT</span>
                                                <span>{totalAmount != null ? formatRupees(totalAmount) : '—'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}

export default EventBookingDetail
